import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
  Button,
  CircularProgress,
  useTheme,
} from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import DirectionsIcon from "@mui/icons-material/Directions";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import { tokens } from "../../../../../../../../../theme";

interface LocationSectionProps {
  eventVenue: string;
  eventCity: string;
  address?: string;
  googleMapsApiKey: string;
  primaryColor?: string;
}

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

export default function LocationSection({
  eventVenue,
  eventCity,
  address,
  googleMapsApiKey,
  primaryColor = "#1976D2",
}: LocationSectionProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);

  const fallbackCenter = useMemo(
    () => ({
      lat: -17.8252,
      lng: 31.0335,
    }),
    [],
  );

  const fullAddress = useMemo(() => {
    return [eventVenue, address, eventCity].filter(Boolean).join(", ");
  }, [eventVenue, address, eventCity]);

  const directionsUrl = useMemo(() => {
    const destination =
      fullAddress || [eventVenue, eventCity].filter(Boolean).join(", ");
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
      destination,
    )}`;
  }, [fullAddress, eventVenue, eventCity]);

  useEffect(() => {
    const fetchCoordinates = async () => {
      if (!address && !eventVenue && !eventCity) {
        setLocation(fallbackCenter);
        return;
      }

      setIsLoading(true);

      try {
        const query = fullAddress || `${eventVenue}, ${eventCity}`;
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
            query,
          )}&key=${googleMapsApiKey}`,
        );

        const data = await response.json();

        if (data.results?.[0]) {
          const { lat, lng } = data.results[0].geometry.location;
          setLocation({ lat, lng });
        } else {
          setLocation(fallbackCenter);
        }
      } catch (error) {
        console.error("Geocoding error:", error);
        setLocation(fallbackCenter);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCoordinates();
  }, [
    address,
    eventVenue,
    eventCity,
    fullAddress,
    googleMapsApiKey,
    fallbackCenter,
  ]);

  const handleGetDirections = () => {
    window.open(directionsUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Box
      sx={{
        py: { xs: 4, md: 6 },
        px: { xs: 2, md: 6 },
        bgcolor: "#FAFBFF",
        borderRadius: 4,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 4 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: "#E3F2FD",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PlaceIcon sx={{ color: primaryColor, fontSize: 22 }} />
        </Box>

        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "#1a1a2e" }}>
            Event Location
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Find your way to the venue
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Stack spacing={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: colors.primary[900],
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: "#E3F2FD",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <HomeWorkIcon sx={{ color: primaryColor }} />
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 600 }}
                  >
                    VENUE
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#1a1a2e" }}
                  >
                    {eventVenue || "To Be Announced"}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: colors.primary[900],
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: "#E3F2FD",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <LocationCityIcon
                    sx={{ color: primaryColor, fontSize: 22 }}
                  />
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 600 }}
                  >
                    ADDRESS
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#1a1a2e" }}
                  >
                    {eventCity || "To Be Announced"}
                  </Typography>

                  {address && (
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", mt: 0.5 }}
                    >
                      {address}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Paper>

            <Button
              variant="contained"
              startIcon={<DirectionsIcon />}
              onClick={handleGetDirections}
              sx={{
                textTransform: "none",
                borderRadius: 3,
                px: 3,
                py: 1.25,
                bgcolor: primaryColor,
                fontWeight: 600,
                boxShadow: "0 4px 14px rgba(25, 118, 210, 0.4)",
                "&:hover": {
                  bgcolor: primaryColor,
                  opacity: 0.9,
                },
              }}
            >
              Get Directions
            </Button>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              minHeight: 350,
              height: "100%",
              border: "1px solid",
              borderColor: "divider",
              position: "relative",
              bgcolor: "#F5F7FB",
            }}
          >
            {isLoading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  bgcolor: "rgba(255,255,255,0.7)",
                  zIndex: 2,
                }}
              >
                <CircularProgress />
              </Box>
            )}

            <LoadScript googleMapsApiKey={googleMapsApiKey}>
              {location && (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  zoom={15}
                  center={location}
                >
                  <Marker position={location} />
                </GoogleMap>
              )}
            </LoadScript>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
